import React from 'react';
import { withRouter,
  Switch, Route } from 'react-router-dom';
import { path, prop } from 'ramda';
import useBreadcrumb from '../../hooks/useBreadcrumb';
import CreateOffer from '../offer/CreateOffer';
import EditOffer from '../offer/EditOffer';
import CreateOfferDocument from '../docs/CreateOfferDocument';
import CreateOfferWidget from '../docs/CreateOfferWidget';
import CreateTagDocument from '../docs/CreateOfferTag';
import CreateGlobalVariableDocument from '../docs/CreateOfferGlobalVariable';
import TagDocument from '../docs/TagDocument';
import EditOfferDocument from '../docs/EditOfferDocument';
import GlobalVariableDocument from '../docs/GlobalVariableDocument';
import { offer, renderWithBreadcrumb, renderWithoutBreadcrumb } from '../helpers/breadCrumb';
import PreviewTagDocument from '../docs/PreviewTagDocument';
import PreviewGlobalVariableDocument from '../docs/PreviewGlobalVariableDocument';
import EditDocument from '../docs/EditDocument';
import EditWidget from '../docs/EditWidget';
import PreviewWidget from '../docs/PreviewWidget';
import WidgetHistory from '../docs/WidgetHistory';
import DocumentHistory from '../docs/DocumentHistory';
import { ROLES } from '../../lib/rights';
import ProtectedRoute from '../../ProtectedRoute';
import OfferHistory from './OfferHistory';

const create = ['/offre/creer', 'Créer une offre'];
const edit = ({ offerId }) => [`/offre/${offerId}/edition`, 'Éditer'];
const editWidget = ({ widgetId, offerId }) => [`/offre/${offerId}/widgets/${widgetId}/editer`, 'Modifier le widget'];
const editDocument = ({ documentId }) => [`/offre/${documentId}/editer`, 'Modifier le document'];
const createTag = ({ offerId }) => [`/offre/${offerId}/edition/tags/creer`, 'Créer un tag'];
const createWidget = ({ offerId }) => [`/offre/${offerId}/edition/widgets/creer`, 'Créer un widget'];
const previewWidget = ({ offerId, widgetId }) => [`/offre/${offerId}/widgets/${widgetId}/preview`, 'Prévisualiser un widget'];
const previewTag = ({ offerId, tagId }) => [`/offre/${offerId}/tags/${tagId}/preview`, 'Prévisualiser un tag'];
const previewGlobalVariable = ({ offerId, globalVariableId }) => [`/offre/${offerId}/tags/${globalVariableId}/preview`, 'Prévisualiser une variable globale'];
const createGlobalVariable = ({ offerId }) => [`/offre/${offerId}/edition/variables-globales/creer`, 'Créer une variable globale'];
const manageTeam = ({ offerId, memberId }) => [`/offre/${offerId}/equipe/${memberId}`, 'Gestion de l\'équipe'];
const history = ({ offerId }) => [`/offre/${offerId}/historique`, 'Historique de l\'offre'];
const historyWidget = ({ offerId, widgetId }) => [`/offre/${offerId}/widgets/${widgetId}/historique`, 'Historique du widget'];
const historyDocument = ({ offerId, documentId }) => [`/offre/${offerId}/documents/${documentId}/historique`, 'Historique du document'];
const editTag = ({ offerId, tagId }) => [`/offre/${offerId}/tags/${tagId}/edition`, 'Modifier le tag'];

const routes = [
  ['/offre/creer', [offer, create]],
  ['/offre/:offerId/edition/tags/creer', [offer, edit, createTag]],
  ['/offre/:offerId/edition/widgets/creer', [offer, edit, createWidget]],
  ['/offre/:offerId/widgets/:widgetId/editer', [offer, edit, editWidget]],
  ['/offre/:offerId/widgets/:widgetId/preview', [offer, edit, previewWidget]],
  ['/offre/:offerId/widgets/:widgetId/historique', [offer, edit, historyWidget]],
  ['/offre/:offerId/documents/:documentId/historique', [offer, edit, editDocument, historyDocument]],
  ['/offre/:documentId/editer', [offer, edit, editDocument]],
  ['/offre/:offerId/tags/:tagId/preview', [offer, edit, previewTag]],
  ['/offre/:offerId/variables-globales/:globalVariableId/preview', [offer, edit, previewGlobalVariable]],
  ['/offre/:offerId/edition/variables-globales/creer', [offer, edit, createGlobalVariable]],
  ['/offre/:documentId/editer', [offer, edit, editDocument]],
  ['/offre/:offerId/edition', [offer, edit]],
  ['/offre/:offerId/equipe/:memberId', [offer, edit, manageTeam]],
  ['/offre/:offerId/historique', [offer, edit, history]],
  ['/offre/:offerId/tags/:tagId/edition', [offer, edit, editTag]]
];

const Routes = ({
  match
}) => {
  const breadcrumb = useBreadcrumb(routes, offer);
  const matchUrl = prop('url', match);
  // Always use `render`, never `component`: `renderWith(out)Breadcrumb` builds a brand
  // new function on every render, so passing it as `component` gives React a new element
  // *type* each time, which unmounts and remounts the whole page (losing every bit of
  // local state) as soon as anything above re-renders — the background QUserQuery
  // refresh in particular. With `render` the returned tree keeps stable types.
  return (
    <Switch>
      <ProtectedRoute path={`${matchUrl}/creer`} render={renderWithBreadcrumb(CreateOffer, breadcrumb)} roles={[ROLES.admin]} />

      <Route path={`${matchUrl}/:documentId/editer`} render={renderWithoutBreadcrumb(EditDocument, breadcrumb)}/>
      <Route path={`${matchUrl}/:offerId/widgets/:widgetId/editer`} render={renderWithBreadcrumb(EditWidget, breadcrumb)}/>

      <Route path={`${matchUrl}/:offerId/edition/widgets/creer`} render={renderWithBreadcrumb(CreateOfferWidget, breadcrumb)} />
      <Route path={`${matchUrl}/:offerId/widgets/:widgetId/preview`} render={renderWithBreadcrumb(PreviewWidget, breadcrumb)} />
      <Route path={`${matchUrl}/:offerId/widgets/:widgetId/historique`} render={renderWithBreadcrumb(WidgetHistory, breadcrumb)} />

      <Route path={`${matchUrl}/:offerId/edition/documents/creer`} render={renderWithoutBreadcrumb(CreateOfferDocument, breadcrumb)} />
      <Route path={`${matchUrl}/:offerId/documents/:documentId/edition`} render={renderWithoutBreadcrumb(EditOfferDocument, breadcrumb)} />
      <Route path={`${matchUrl}/:offerId/documents/:documentId/historique`} render={renderWithBreadcrumb(DocumentHistory, breadcrumb)} />
      <Route path={`${matchUrl}/:offerId/documents-importes/:documentId/edition`} render={renderWithBreadcrumb(EditOfferDocument, breadcrumb)} />

      <Route path={`${matchUrl}/:offerId/edition/tags/creer`} render={renderWithBreadcrumb(CreateTagDocument, breadcrumb)} />
      <Route path={`${matchUrl}/:offerId/tags/:tagId/edition`} render={renderWithBreadcrumb(TagDocument, breadcrumb)} />
      <Route path={`${matchUrl}/:offerId/tags/:tagId/preview`} render={renderWithBreadcrumb(PreviewTagDocument, breadcrumb)} />

      <Route path={`${matchUrl}/:offerId/edition/variables-globales/creer`} render={renderWithBreadcrumb(CreateGlobalVariableDocument, breadcrumb)} />
      <Route path={`${matchUrl}/:offerId/variables-globales/:globalVariableId/edition`} render={renderWithBreadcrumb(GlobalVariableDocument, breadcrumb)} />
      <Route path={`${matchUrl}/:offerId/variables-globales/:globalVariableId/preview`} render={renderWithBreadcrumb(PreviewGlobalVariableDocument, breadcrumb)} />

      <Route
        path={`${matchUrl}/:offerId/edition`}
        render={renderWithBreadcrumb(EditOffer, breadcrumb, path(['match', 'params', 'offerId']))}
      />
      <Route path={`${matchUrl}/:offerId/historique`} render={renderWithBreadcrumb(OfferHistory, breadcrumb)} />
    </Switch>
  );
};

export default withRouter(Routes);
